import { useEffect, useMemo, useState } from "react";
import { smoothScrollTo } from "../../../../utils/smoothScroll";
import Conversation from "../conversation/Conversation";

import { MessageType, ProductType } from "../../../../utils/types";
import { ConversationProvider } from "../../../../contexts/ConversationContext";

// ============ DEMO DATA ============

// Ecommerce Demo
const ecommerceProducts: ProductType[] = [
  { id: 1, name: 'Nike Air Max 90', price: 'R$ 899', image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=200&h=200&fit=crop', discount: '-15%' },
  { id: 2, name: 'Adidas Ultraboost', price: 'R$ 1.199', image: 'https://images.unsplash.com/photo-1608231387042-66d1773070a5?w=200&h=200&fit=crop' },
  { id: 3, name: 'New Balance 574', price: 'R$ 699', image: 'https://images.unsplash.com/photo-1539185441755-769473a23570?w=200&h=200&fit=crop', discount: '-20%' },
];

const ecommerceMessages: MessageType[] = [
  { type: 'user', text: 'Oi! Quero ver tênis em promoção', format: 'text' },
  { type: 'receiver', text: `Olá! 👋 Temos ótimas opções para você!`, format: 'text' },
  { type: 'receiver', text: 'Veja nossos destaques →', format: 'carousel', products: ecommerceProducts },
  { type: 'user', text: 'Quero o Nike Air Max!', format: 'selection', selectedProduct: ecommerceProducts[0] },
  { type: 'receiver', text: `Excelente escolha! 🔥 O Air Max 90 é um dos mais vendidos.\n\nPosso reservar para você?`, format: 'text' },
  { type: 'user', text: 'Sim!', format: 'text' },
  { type: 'receiver', text: `✅ Reserva confirmada! Envio o link de pagamento agora.`, format: 'text' },
];

// Healthcare/Document Demo
const healthcareMessages: MessageType[] = [
  { type: 'user', text: 'Preciso dos resultados do meu exame', format: 'text' },
  { type: 'receiver', text: `Claro! Para isso, preciso que você me informe seu CPF.`, format: 'text' },
  { type: 'user', text: '123.456.789-98', format: 'text' },
  { type: 'receiver', text: `Aguarde, estou buscando seu documento...`, format: 'text' },
  { type: 'receiver', text: `Resultados_Exame.pdf`, format: 'file' },
  { type: 'receiver', text: `Aqui está! Precisa de mais alguma ajuda?`, format: 'text' },
  { type: 'user', text: 'Não, obrigado!', format: 'text' },
  { type: 'receiver', text: `Por nada! Estou sempre à disposição. 😊`, format: 'text' },
];

// Support/Audio Demo
const supportMessages: MessageType[] = [
  { type: 'user', text: 'Olá, tenho uma dúvida sobre meu pedido', format: 'text' },
  { type: 'receiver', text: `Olá! Claro, estou aqui para ajudar. Qual é o número do seu pedido?`, format: 'text' },
  { type: 'user', text: '#45892', format: 'text' },
  { type: 'receiver', text: `Encontrei! Seu pedido está em separação e será enviado hoje. 📦`, format: 'text' },
  { type: 'user', text: '0:12', format: 'audio' },
  { type: 'receiver', text: `Entendi sua mensagem de voz! O prazo de entrega é de 3-5 dias úteis após o envio.`, format: 'text' },
  { type: 'user', text: 'Perfeito, obrigado!', format: 'text' },
  { type: 'receiver', text: `Disponha! Qualquer dúvida, é só chamar. 🚀`, format: 'text' },
];

// Scheduling Demo
const schedulingMessages: MessageType[] = [
  { type: 'user', text: 'Quero agendar uma consulta', format: 'text' },
  { type: 'receiver', text: `Olá! Vou te ajudar a agendar. Qual especialidade você precisa?`, format: 'text' },
  { type: 'user', text: 'Dermatologista', format: 'text' },
  { type: 'receiver', text: `Temos horários disponíveis:\n\n📅 Seg 20/01 - 14h\n📅 Ter 21/01 - 10h\n📅 Qua 22/01 - 16h\n\nQual prefere?`, format: 'text' },
  { type: 'user', text: 'Terça às 10h', format: 'text' },
  { type: 'receiver', text: `✅ Agendado!\n\nDra. Ana Silva\n21/01 às 10:00\nClínica Central\n\nEnviei a confirmação por email!`, format: 'text' },
];

// Demo configurations
const demos = [
  {
    id: 'ecommerce',
    label: 'Vendas',
    icon: '🛒',
    messages: ecommerceMessages,
    userImage: 'first-user-profile.png',
  },
  {
    id: 'healthcare',
    label: 'Saúde',
    icon: '🏥',
    messages: healthcareMessages,
    userImage: 'first-user-profile.png',
  },
  {
    id: 'support',
    label: 'Suporte',
    icon: '🎧',
    messages: supportMessages,
    userImage: 'first-user-profile.png',
  },
  {
    id: 'scheduling',
    label: 'Agenda',
    icon: '📅',
    messages: schedulingMessages,
    userImage: 'first-user-profile.png',
  },
];

const First = () => {
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typewriterSpeed] = useState(120);
  const [shouldStartTyping, setShouldStartTyping] = useState(true);
  const [activeDemo, setActiveDemo] = useState(0);
  const [demoKey, setDemoKey] = useState(0);

  const words = useMemo(() => ["Gestão.", "Operação.", "Vendas.", "Atendimento.", "Inovação."], []);

  useEffect(() => {
    const currentWord = words[index];

    if (!shouldStartTyping) return;

    const handleTyping = () => {
      if (!isDeleting && subIndex === currentWord.length + 1) {
        setShouldStartTyping(false);
        setTimeout(() => {
          setIsDeleting(true);
          setShouldStartTyping(true);
        }, 2000);
        return;
      }

      if (isDeleting && subIndex === 0) {
        setIsDeleting(false);
        setIndex((prevIndex) => (prevIndex + 1) % words.length);
      }

      const nextSubIndex = isDeleting ? subIndex - 1 : subIndex + 1;
      setSubIndex(nextSubIndex);
    };

    const typingTimeout = setTimeout(handleTyping, typewriterSpeed);

    return () => clearTimeout(typingTimeout);
  }, [subIndex, isDeleting, shouldStartTyping, words, index, typewriterSpeed]);


  const handleDemoChange = (newIndex: number) => {
    setActiveDemo(newIndex);
    setDemoKey((prev) => prev + 1);
  };

  const currentDemo = demos[activeDemo];

  return (
    <section className="relative min-h-screen bg-[#f6f6f6] overflow-hidden">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-grid opacity-40" />

      {/* Gradient Orbs */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#c1d5ed] rounded-full filter blur-[128px] opacity-40 animate-pulse-glow" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[#1f2fd6] rounded-full filter blur-[128px] opacity-20 animate-pulse-glow" style={{ animationDelay: '1.5s' }} />

      <div className="relative z-10 mx-auto max-w-7xl px-6 pt-32 pb-20 lg:pt-40 lg:pb-32">
        <div className="lg:grid lg:grid-cols-12 lg:gap-12 items-center">

          {/* Left Content */}
          <div className="lg:col-span-6 xl:col-span-5">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-light mb-8">
              <span className="w-2 h-2 bg-[#1f2fd6] rounded-full animate-pulse" />
              <span className="text-sm text-[#0c1aa6]">Arquitetos de Soluções de IA</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              <span className="gradient-text">O futuro</span>
              <br />
              <span className="text-[#212223]">opera agora.</span>
            </h1>

            {/* Typewriter Subheadline */}
            <p className="text-xl sm:text-2xl text-[#212223] mb-4">
              IA que transforma{' '}
              <span className="text-[#1f2fd6] font-semibold">
                {`${words[index].substring(0, subIndex)}`}
              </span>
              <span className="inline-block w-0.5 h-6 bg-[#1f2fd6] ml-1 animate-blink align-middle" />
            </p>

            {/* Description */}
            <p className="text-base sm:text-lg text-[#5a5a5a] mb-10 max-w-lg">
              Decodificamos a complexidade dos negócios e aplicamos IA de forma
              estratégica para gerar crescimento, eficiência e vantagem competitiva.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => smoothScrollTo('labs')}
                className="group relative px-8 py-4 bg-[#1f2fd6] text-white font-medium rounded-xl overflow-hidden transition-all hover:shadow-lg hover:shadow-[#1f2fd6]/25"
              >
                <span className="relative z-10">Conhecer Labs</span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#1f2fd6] to-[#0c1aa6] opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <button
                onClick={() => smoothScrollTo('go')}
                className="px-8 py-4 border border-[#1f2fd6]/40 text-[#0c1aa6] font-medium rounded-xl hover:bg-[#1f2fd6]/10 hover:border-[#1f2fd6]/60 transition-all"
              >
                Conhecer Go
              </button>
            </div>
          </div>

          {/* Right Content - Phone Mockup */}
          <div className="lg:col-span-6 xl:col-span-7 mt-16 lg:mt-0">
            <div className="relative flex justify-center lg:justify-end">

              {/* Floating Asterisk Symbol */}
              <div className="absolute -top-8 -left-4 lg:left-8 w-24 h-24 animate-float">
                <svg viewBox="0 0 174 175" fill="none" className="w-full h-full">
                  <path
                    d="M86.36,0l.33,73.88,52.33-51.84,14.09,14.09-52,52.01.33.65h67.24v19.44h-68.05l52.14,52.06-14.39,14.38-44.89-44.23c-10.73-11.35,4.46-43.07-14.64-48.22-12.79-3.44-29.48,3.88-39.63-6.71L0,36.29l14.22-14.23,52.38,51.82V0h19.77Z"
                    fill="#1f2fd6"
                    className="drop-shadow-[0_0_20px_rgba(31,47,214,0.5)]"
                  />
                  <path
                    d="M68.91,144.75c-.71-13.96-1.42-27.92-2.14-41.88-13.21-.38-26.41-.76-39.62-1.15,2.78-3.39,5.57-6.78,8.35-10.17,10.79.17,21.57.33,32.36.5.78.08,4.51.57,7.22,3.86,2.21,2.69,2.41,5.65,2.43,6.59.31,10.73.62,21.45.93,32.18-3.18,3.36-6.35,6.72-9.53,10.08Z"
                    fill="#1f2fd6"
                  />
                </svg>
              </div>

              {/* Phone Container */}
              <div className="relative w-[320px] sm:w-[380px]">
                {/* Phone Frame */}
                <div className="relative rounded-[3rem] overflow-hidden glass glow-blue">
                  {/* Phone Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-[#212223] rounded-b-2xl z-20" />

                  {/* Screen Content */}
                  <div className="bg-[#0c1aa6] pt-8 pb-4">
                    {/* App Header */}
                    <div className="flex justify-center px-6 py-4">
                      <div className="flex items-center gap-2">
                        <svg className="h-6 w-6 text-white" viewBox="0 0 174 175" fill="currentColor">
                          <path d="M86.36,0l.33,73.88,52.33-51.84,14.09,14.09-52,52.01.33.65h67.24v19.44h-68.05l52.14,52.06-14.39,14.38-44.89-44.23c-10.73-11.35,4.46-43.07-14.64-48.22-12.79-3.44-29.48,3.88-39.63-6.71L0,36.29l14.22-14.23,52.38,51.82V0h19.77Z"/>
                          <path d="M68.91,144.75c-.71-13.96-1.42-27.92-2.14-41.88-13.21-.38-26.41-.76-39.62-1.15,2.78-3.39,5.57-6.78,8.35-10.17,10.79.17,21.57.33,32.36.5.78.08,4.51.57,7.22,3.86,2.21,2.69,2.41,5.65,2.43,6.59.31,10.73.62,21.45.93,32.18-3.18,3.36-6.35,6.72-9.53,10.08Z"/>
                        </svg>
                        <span className="text-white font-semibold">Arkspace</span>
                      </div>
                    </div>

                    {/* Chat Area */}
                    <div className="bg-white rounded-t-3xl mx-2">
                      <div className="p-4 h-[480px] overflow-hidden">
                        <ConversationProvider key={demoKey} initialMessages={currentDemo.messages}>
                          <Conversation
                            className="h-full"
                            messages={currentDemo.messages}
                            userImage={currentDemo.userImage}
                          />
                        </ConversationProvider>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Demo Selector - Below Phone */}
                <div className="relative z-10 mt-6 flex justify-center">
                  <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/90 border border-[#1f2fd6]/20 shadow-lg shadow-[#1f2fd6]/10">
                    {demos.map((demo, idx) => (
                      <button
                        key={demo.id}
                        onClick={() => handleDemoChange(idx)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                          activeDemo === idx
                            ? 'bg-[#1f2fd6] text-white'
                            : 'text-[#5a5a5a] hover:text-[#0c1aa6]'
                        }`}
                      >
                        <span className="text-sm">{demo.icon}</span>
                        <span>{demo.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-[#1f2fd6]/20 rounded-full pointer-events-none" />
                <div className="absolute -bottom-8 -right-8 w-48 h-48 border border-[#1f2fd6]/10 rounded-full pointer-events-none" />
              </div>

              {/* Spinning Rings - Background */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] -z-10">
                <svg viewBox="0 0 500 500" fill="none" className="absolute inset-0 w-full h-full animate-spin-slow opacity-30">
                  <circle cx="250" cy="250" r="200" stroke="#1f2fd6" strokeWidth="1" strokeDasharray="10 10" />
                </svg>
                <svg viewBox="0 0 500 500" fill="none" className="absolute inset-0 w-full h-full animate-spin-reverse-slower opacity-20">
                  <circle cx="250" cy="250" r="240" stroke="#c1d5ed" strokeWidth="1" strokeDasharray="5 15" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-xs text-[#5a5a5a] uppercase tracking-widest">Scroll</span>
          <svg className="w-5 h-5 text-[#1f2fd6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </section>
  )
}

export default First;
